import React, { useState, useEffect } from 'react';
import { Combobox } from '@headlessui/react';
import { useDebounce } from 'use-debounce';
import { Search, Loader2, MapPin } from 'lucide-react';
import { useVariable } from '@softwareimaging/backstage';
import { 
  searchLoqateAddresses, 
  retrieveLoqateAddress, 
  LoqateFindItem 
} from '../../../../services/api/loqate';

export interface AutocompleteAddressData {
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  country?: string;
  postcode: string;
}

interface AddressAutocompleteProps {
  label?: string;
  placeholder?: string;
  onChange: (address: AutocompleteAddressData) => void;
  disabled?: boolean;
  className?: string;
}

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  label = 'Search Address',
  placeholder = 'Start typing an address...',
  onChange,
  disabled = false,
  className = '',
}) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 300);
  const [options, setOptions] = useState<LoqateFindItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isRetrieving, setIsRetrieving] = useState(false);

  const loqateApiKey = useVariable('loqateApiKey') as string;

  const [containerId, setContainerId] = useState<string | undefined>(undefined);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // Search effect
  useEffect(() => {
    let active = true;

    const fetchAddresses = async () => {
      if ((!debouncedQuery || debouncedQuery.length < 3) && !containerId) {
        setOptions([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const results = await searchLoqateAddresses(debouncedQuery, loqateApiKey, containerId);
        if (active) {
          setOptions(results);
        }
      } catch (error) {
        console.error(error);
        if (active) {
          setOptions([]);
        }
      } finally {
        if (active) {
          setIsSearching(false);
        }
      }
    };

    fetchAddresses().catch(console.error);

    return () => {
      active = false;
    };
  }, [debouncedQuery, containerId]);

  const handleSelect = async (selectedItem: LoqateFindItem | null) => {
    if (!selectedItem) return;
    
    if (selectedItem.Type !== 'Address') {
      // It's a container. Set the containerId to drill down.
      setContainerId(selectedItem.Id);
      setQuery(''); // Clear text so it retrieves all children in the container
      
      // Re-open the combobox dropdown since selecting an item automatically closes it
      setTimeout(() => {
        buttonRef.current?.click();
      }, 0);
      return;
    }

    setIsRetrieving(true);
    // Setting the query to the text so the input shows the selected address line
    setQuery(selectedItem.Text + (selectedItem.Description ? `, ${selectedItem.Description}` : ''));

    try {
      const details = await retrieveLoqateAddress(selectedItem.Id, loqateApiKey);
      if (details) {
        const mappedAddress: AutocompleteAddressData = {
          line1: details.Company ? `${details.Company}, ${details.Line1}` : details.Line1,
          line2: details.Line2 || undefined,
          city: details.PostTown || details.City || '',
          state: details.Province || details.PostTown || 'N/A',
          country: details.CountryName || undefined,
          postcode: details.PostalCode,
        };
        onChange(mappedAddress);
      }
    } catch (error) {
      console.error('Failed to retrieve full address details', error);
    } finally {
      setIsRetrieving(false);
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {label}
        </label>
      )}
      <Combobox onChange={handleSelect} disabled={disabled || isRetrieving}>
        <div className="relative">
          <div className="relative w-full cursor-default overflow-hidden rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-left shadow-sm focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 sm:text-sm">
            <Combobox.Input
              className="w-full border-none py-2 pl-10 pr-10 text-sm leading-5 text-slate-900 dark:text-slate-100 bg-transparent focus:ring-0 outline-none"
              displayValue={(item: LoqateFindItem | null) => 
                item ? `${item.Text}${item.Description ? `, ${item.Description}` : ''}` : query
              }
              onChange={(event) => {
                setQuery(event.target.value);
                if (containerId) setContainerId(undefined); // Reset container if user starts typing a new search
              }}
              placeholder={placeholder}
            />
            
            <Combobox.Button ref={buttonRef} className="hidden" aria-hidden="true" />
            
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="h-4 w-4 text-slate-400" aria-hidden="true" />
            </div>

            {(isSearching || isRetrieving) && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <Loader2 className="h-4 w-4 text-slate-400 animate-spin" aria-hidden="true" />
              </div>
            )}
          </div>

          <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-slate-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50">
            {options.length === 0 && query.length >= 3 && !isSearching ? (
              <div className="relative cursor-default select-none py-2 px-4 text-slate-700 dark:text-slate-400">
                No addresses found.
              </div>
            ) : (
              options.map((option) => (
                <Combobox.Option
                  key={option.Id}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                      active ? 'bg-indigo-600 text-white' : 'text-slate-900 dark:text-slate-100'
                    }`
                  }
                  value={option}
                >
                  {({ active }) => (
                    <>
                      <span className={`block truncate ${active ? 'font-medium' : 'font-normal'}`}>
                        {option.Text} {option.Description && <span className={`ml-2 text-xs ${active ? 'text-indigo-200' : 'text-slate-500'}`}>{option.Description}</span>}
                      </span>
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                        <MapPin className={`h-4 w-4 ${active ? 'text-white' : 'text-slate-400'}`} aria-hidden="true" />
                      </span>
                    </>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </div>
      </Combobox>
    </div>
  );
};

export default AddressAutocomplete;
